import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { get, post, put, del, postForm } from "../services/apiClient";
import type { PremiumService } from "@postnatal-care/shared-types";

export const usePremiumServices = (businessId: string) => {
  return useQuery<PremiumService[]>({
    queryKey: ["premium-services", businessId],
    queryFn: () => get(`/premium-services?businessId=${businessId}`),
    enabled: !!businessId,
  });
};

export const useCreatePremiumService = (businessId: string) => {
  const queryClient = useQueryClient();
  return useMutation<
    PremiumService,
    Error,
    Omit<PremiumService, "id" | "createdAt" | "updatedAt">
  >({
    mutationFn: (data) => post("/premium-services", data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["premium-services", businessId],
      });
    },
  });
};

export const useUpdatePremiumService = (businessId: string) => {
  const queryClient = useQueryClient();
  return useMutation<
    PremiumService,
    Error,
    { id: string; data: Partial<PremiumService> }
  >({
    mutationFn: ({ id, data }) => put(`/premium-services/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["premium-services", businessId],
      });
    },
  });
};

export const useDeletePremiumService = (businessId: string) => {
  const queryClient = useQueryClient();
  return useMutation<{ success: boolean }, Error, string>({
    mutationFn: (id) => del(`/premium-services/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["premium-services", businessId],
      });
    },
  });
};

export const useUploadPremiumServicePhoto = (businessId: string) => {
  const queryClient = useQueryClient();
  return useMutation<PremiumService, Error, { id: string; file: File }>({
    mutationFn: ({ id, file }) => {
      const form = new FormData();
      form.append("file", file);
      return postForm<PremiumService>(`/premium-services/${id}/photo`, form);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["premium-services", businessId],
      });
    },
  });
};
